/**
 * @fileoverview Upload button for the Files tab of a container list.
 *
 * Renders an "Upload" action button in the RecordTable toolbar's left action
 * group (via `options.customLeftToolbarChildren`, before the Move button) that
 * lets the user pick one or more local files and create FILE records from them
 * directly in the container the Files tab is scoped to (workspace / project /
 * bundle) — without leaving the list.
 *
 * The upload path mirrors the sidebar "Create → Upload file" flow
 * (components/Layout/Sidebar/components/CreateNew): read each file as a binary
 * string, base64-encode it, and dispatch START_CREATE_RESOURCE for the `file`
 * resource with the container location fields. Progress is tracked via the
 * shared Upload reducer (feeding the Topbar upload popover) and success/failure
 * is surfaced via NotificationService toasts. On completion the list is
 * refreshed via the `onUploaded` callback.
 *
 * @module UploadFileButton
 */

import { Buffer } from "buffer";
import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import ExpandableNotification from "components/Notification/ExpandableNotification";
import NotificationService from "components/Notification/Notification.service";
import { actions as resourcesActions } from "containers/Resources/Resources.reducer";
import { actions as uploadActions } from "containers/Upload/Upload.reducer";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useFilePicker } from "use-file-picker";
import { FileContent } from "use-file-picker/dist/interfaces";
import { v4 as uuidv4 } from "uuid";

/**
 * The container location a FILE record is created in. Mirrors the shape
 * produced by getLocationField (MoveToLocation): a workspace-root upload sets
 * both ids to null, a project/bundle upload sets the respective id.
 */
export type FileUploadLocation = {
    project_id?: string | null;
    bundle_id?: string | null;
};

type UploadFileButtonProps = {
    /** The container the uploaded FILE records are created in. */
    location: FileUploadLocation;
    /** Called after all uploads settle (e.g. to refresh the file list). */
    onUploaded?: () => void;
};

/**
 * Upload action button for the Files tab. Picks local files and creates a FILE
 * record per file in the given container location.
 */
export const UploadFileButton = ({ location, onUploaded }: UploadFileButtonProps) => {
    const dispatch = useDispatch();

    const [openFileSelector, { filesContent, loading }] = useFilePicker({
        accept: "*",
        readAs: "BinaryString",
        multiple: true,
    });

    /**
     * Upload the picked files: create one FILE record per file, track progress
     * via the Upload reducer, and surface success/failure via toasts.
     */
    const upload = (fileInfos: FileContent[]) => {
        if (!fileInfos || fileInfos.length <= 0) return;
        const uploadId = uuidv4();

        dispatch({
            type: uploadActions.START_UPLOAD,
            fileInfos,
            uploadId,
            recordType: "file",
        });

        const errors: Set<{ fileName: string; error: any }> = new Set();
        let successUploads: string[] = [];

        const promises = fileInfos.map((fileInfo) =>
            new Promise((resolve, reject) => {
                dispatch({
                    type: resourcesActions.START_CREATE_RESOURCE,
                    promise: { resolve, reject },
                    resourceName: "file",
                    variables: {
                        ...location,
                        name: fileInfo.name,
                        content: Buffer.from(fileInfo.content, "binary").toString("base64"),
                    },
                });
            })
                .then((resource: any) => {
                    successUploads.push(fileInfo.name);
                    dispatch({
                        type: uploadActions.FILE_UPLOADED,
                        uploadId,
                        recordId: resource.id,
                        recordName: resource.name,
                        fileName: fileInfo.name,
                    });
                })
                .catch((error) => {
                    errors.add({ fileName: fileInfo.name, error });
                    dispatch({
                        uploadId,
                        type: uploadActions.FILE_UPLOAD_FAILED,
                        fileName: fileInfo.name,
                    });
                })
        );

        Promise.allSettled(promises).then(() => {
            dispatch({
                type: uploadActions.UPLOAD_FINISHED,
                uploadId,
            });

            if (errors.size > 0) {
                for (const error of errors) {
                    const displayError =
                        typeof error.error === "object"
                            ? `${
                                  (error.error as any).title
                                      ? `${(error.error as any).title}: `
                                      : ""
                              }${error.error.message}`
                            : error.error;
                    NotificationService.warn(
                        <ExpandableNotification
                            title={`${error.fileName} failed to upload`}
                            details={displayError}
                        />
                    );
                    successUploads = successUploads.filter((value) => value !== error.fileName);
                }
            }

            if (successUploads.length > 0) {
                NotificationService.success(
                    <ExpandableNotification
                        title="Upload finished"
                        subtitle={`${successUploads.length} file(s) uploaded`}
                        details={successUploads.join(", ")}
                    />
                );
            }

            onUploaded?.();
        });
    };

    // Kick off the upload once the file picker has read the selected files.
    useEffect(() => {
        if (!filesContent || filesContent.length <= 0) return;
        upload(filesContent);
        // upload() is stable enough for this effect; adding it (and its captured
        // deps) would re-run on every render — mirrors the CreateNew pattern.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filesContent]);

    return (
        <Button
            variation="dark"
            label="Upload"
            title="Upload file(s)"
            icon={IconCollection.uploadOutline}
            loading={loading}
            disabled={loading}
            onClick={openFileSelector}
        />
    );
};

export default UploadFileButton;
